// Der Ereignisstrom des Dienstes.
//
// Meldet, DASS sich etwas getan hat — nicht WAS. Die Daten holt sich die
// Ansicht über den vorhandenen Endpunkt, und dort greift die Rechteprüfung.
//
// EINE Verbindung für den ganzen Tab, nicht eine je Ansicht: Browser erlauben
// über HTTP/1.1 nur sechs Verbindungen zur selben Domain. Die Recherche-Seite
// allein hat vier Abfragen — mit je eigener Verbindung blockierte sich die
// Anwendung selbst, und zwar erst im Betrieb.
//
// `fetch` und nicht `EventSource`: Die Browser-API kann KEINE eigenen Header
// setzen, und wir melden uns mit `Authorization: Basic …` an. Der Token in
// der Adresse wäre der andere Weg — er landete in Zugriffsprotokollen und im
// Browserverlauf.

import { authHeader } from './auth'

const URL = '/api/ui/v1/events'

/** Ein Hinweis: In diesem Kanal hat sich etwas getan. */
export interface StreamEvent {
  topic: string
  id: number | null
}

type Handler = (event: StreamEvent) => void

/**
 * Wie lange nach einem Abbruch gewartet wird, bevor neu verbunden wird.
 *
 * Wachsend bis zum Höchstwert: Bei einem Neustart des Dienstes soll der
 * Bildschirm nicht im Sekundentakt anklopfen. `EventSource` bringt einen
 * festen Abstand mit — das ist einer der Gründe, warum wir es selbst machen.
 */
const RETRY_START = 1000
const RETRY_MAX = 30_000

const handlers = new Map<string, Set<Handler>>()

let controller: AbortController | null = null
let retryDelay = RETRY_START
let retryTimer: number | null = null

/**
 * Hört auf einen Kanal. Gibt die Abmeldung zurück.
 *
 * Die Verbindung entsteht beim ersten Abonnenten und endet mit dem letzten —
 * eine offene Leitung ohne Zuhörer wäre nur ein Heartbeat ins Leere.
 */
export function subscribe(topic: string, handler: Handler): () => void {
  const fuerKanal = handlers.get(topic) ?? new Set<Handler>()
  fuerKanal.add(handler)
  handlers.set(topic, fuerKanal)

  connect()

  return () => {
    fuerKanal.delete(handler)
    if (fuerKanal.size === 0) handlers.delete(topic)
    if (handlers.size === 0) disconnect()
  }
}

function connect(): void {
  if (controller !== null || handlers.size === 0) return
  if (!authHeader()) return

  controller = new AbortController()
  void read(controller)
}

function disconnect(): void {
  controller?.abort()
  controller = null
  if (retryTimer !== null) {
    window.clearTimeout(retryTimer)
    retryTimer = null
  }
}

async function read(own: AbortController): Promise<void> {
  try {
    const response = await fetch(URL, {
      headers: { Authorization: authHeader() ?? '' },
      signal: own.signal,
    })
    if (!response.ok || !response.body) {
      throw new Error(`Ereignisstrom: ${response.status}`)
    }

    // Die Verbindung steht — der nächste Abbruch darf wieder schnell
    // nachfassen.
    retryDelay = RETRY_START

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let puffer = ''

    for (;;) {
      const { done, value } = await reader.read()
      if (done) break

      puffer += decoder.decode(value, { stream: true })

      // Ein Ereignis endet mit einer Leerzeile. Was danach kommt, bleibt im
      // Puffer: Ein Datenpaket kann mitten in einem Ereignis enden.
      let grenze = puffer.indexOf('\n\n')
      while (grenze !== -1) {
        handle(puffer.slice(0, grenze))
        puffer = puffer.slice(grenze + 2)
        grenze = puffer.indexOf('\n\n')
      }
    }
    throw new Error('Ereignisstrom beendet')
  } catch (cause) {
    if (own.signal.aborted) return
    scheduleRetry(own)
  }
}

function scheduleRetry(own: AbortController): void {
  if (controller !== own || handlers.size === 0) return
  controller = null

  const delay = retryDelay
  retryDelay = Math.min(retryDelay * 2, RETRY_MAX)
  retryTimer = window.setTimeout(() => {
    retryTimer = null
    connect()
  }, delay)
}

/**
 * Ein Block aus dem Strom.
 *
 * Zeilen, die mit ':' beginnen, sind Kommentare — der Heartbeat, der die
 * Leitung offen hält. `hello` meldet nur, dass die Verbindung steht.
 */
function handle(block: string): void {
  let name = 'message'
  let daten = ''

  for (const zeile of block.split('\n')) {
    if (zeile.startsWith(':')) continue
    if (zeile.startsWith('event:')) name = zeile.slice(6).trim()
    else if (zeile.startsWith('data:')) daten += zeile.slice(5).trim()
  }

  if (name === 'hello' || !daten) return

  try {
    const event = JSON.parse(daten) as StreamEvent
    for (const handler of handlers.get(event.topic) ?? []) handler(event)
  } catch {
    /* Kein gültiges JSON — nichts zu tun, der nächste Hinweis kommt. */
  }
}

/** Beim Abmelden: Die Verbindung darf die Zugangsdaten nicht überleben. */
export function closeEventStream(): void {
  handlers.clear()
  disconnect()
  retryDelay = RETRY_START
}
